import {
  ETaxDocTypeGroup,
  ETaxDocumentStatus,
  ETaxKtbDocumentStatus,
} from "../../types/e-tax.type";

// ---- Payment type (source bill_type) ----
// Scopes the detail list by how the bill was paid. It no longer says anything about
// whether a document is electronic — a shop asks for an electronic invoice or it does
// not, and either kind of shop can pay cash. `CHANNEL` below is that question.
export const PAYMENT_TYPE = {
  CASH: "cashsale",
  CREDIT: "invoice",
} as const;

// ---- Channel (ช่องทาง) ----
// How the bill's tax invoice is delivered, read from the bill's own eTax snapshot.
export const CHANNEL = {
  ETAX: "ETAX",
  PAPER: "PAPER",
} as const;

export const CHANNEL_LABEL: Record<string, string> = {
  [CHANNEL.ETAX]: "eTax",
  [CHANNEL.PAPER]: "กระดาษ",
};

export const CHANNEL_FILTER: { label: string; value: string }[] = [
  { label: "ทั้งหมด", value: "all" },
  { label: "eTax", value: CHANNEL.ETAX },
  { label: "กระดาษ", value: CHANNEL.PAPER },
];

// ---- Document type chips (INV / DN / CN) ----

export const DOC_TYPE_LABEL: Record<ETaxDocTypeGroup, string> = {
  INV: "ใบกำกับภาษี",
  DN: "ใบเพิ่มหนี้",
  CN: "ใบลดหนี้",
};

export const DOC_TYPE_CHIP_COLOR: Record<
  ETaxDocTypeGroup,
  { color: string; bg: string }
> = {
  INV: { color: "#5C69E8", bg: "#ECEEFD" },
  DN: { color: "#45BE77", bg: "#E7F7EE" },
  CN: { color: "#FB3B3B", bg: "#FEEAEA" },
};

// Only INV (tax invoice) and CN (credit note) are produced by the backend
// candidate query; DN (debit note) has no source in the current schema, so it is
// intentionally not offered as a filter option (it would always return 0 rows).
export const DOC_TYPE_FILTER: { label: string; value: ETaxDocTypeGroup | "all" }[] =
  [
    { label: "ทั้งหมด", value: "all" },
    { label: "ใบกำกับภาษี", value: "INV" },
    { label: "ใบลดหนี้", value: "CN" },
  ];

// The notes list: ใบลดหนี้ and ใบเพิ่มหนี้, and no tax invoice.
export const NOTE_DOC_TYPE_FILTER: { label: string; value: ETaxDocTypeGroup | "all" }[] = [
  { label: "ทั้งหมด", value: "all" },
  { label: DOC_TYPE_LABEL.CN, value: "CN" },
  { label: DOC_TYPE_LABEL.DN, value: "DN" },
];

// ---- Bill status (from the Sale Daily Report) ----

type StatusMeta = { label: string; color: string };

const DEFAULT_STATUS_META: StatusMeta = { label: "-", color: "#6a6a6a" };

const BILL_STATUS_META: Record<string, StatusMeta> = {
  approve: { label: "สำเร็จ", color: "#45BE77" },
  pending: { label: "รอตรวจสอบ", color: "#FDA42C" },
  paid: { label: "จ่ายแล้ว", color: "#FDA42C" },
  pending_cancel: { label: "ขอยกเลิกบิล", color: "#FB3B3B" },
  cancel: { label: "ยกเลิก", color: "#FB3B3B" },
};

export const getBillStatusMeta = (status: string | null | undefined): StatusMeta =>
  (status && BILL_STATUS_META[status]) || DEFAULT_STATUS_META;

// ---- E-Tax sending status (null = not yet sent / ค้างส่ง) ----

const ETAX_STATUS_META: Record<ETaxDocumentStatus, StatusMeta> = {
  COMPLETED: { label: "ตัวแทนนำส่งได้รับแล้ว", color: "#45BE77" },
  IN_PROGRESS: { label: "รอตัวแทนนำส่งยืนยัน", color: "#FDA42C" },
  FAILED: { label: "ส่งไม่สำเร็จ", color: "#FB3B3B" },
};

/**
 * How far the document actually got, when KTB has said.
 *
 * The three coarse statuses above cannot tell "we sent it and heard nothing" from "the
 * agent has it" from "it has been filed with the Revenue Department" — and those are weeks
 * apart. The lifecycle status is already on the row; when it is there it decides the label,
 * because it is the more specific truth.
 */
// RECEIVE and SUBMIT_RD are still กำลังดำเนินการ (etax_status IN_PROGRESS): the agent has the
// document but has not signed it yet, or the Revenue Department has not answered yet. They
// wear the in-progress amber and say what they are waiting for, never the success green.
const IN_PROGRESS_COLOR = "#FDA42C";

const KTB_PROGRESS_META: Record<ETaxKtbDocumentStatus, StatusMeta> = {
  RECEIVE: { label: "ตัวแทนได้รับแล้ว รอออกเอกสาร", color: IN_PROGRESS_COLOR },
  SIGNED: { label: "ออกเอกสารแล้ว", color: "#45BE77" },
  SUBMIT_RD: { label: "ยื่นสรรพากรแล้ว รอผลตอบรับ", color: IN_PROGRESS_COLOR },
  RD_ACCEPT: { label: "กรมสรรพากรตอบรับ", color: "#45BE77" },
  SIGNED_FAIL: { label: "ส่งไม่สำเร็จ", color: "#FB3B3B" },
  RD_REJECT: { label: "กรมสรรพากรปฏิเสธ", color: "#FB3B3B" },
  CANCEL: { label: "ยกเลิกการนำส่งแล้ว", color: "#6a6a6a" },
};

const NOT_SENT_META: StatusMeta = { label: "ค้างส่ง", color: "#6a6a6a" };

// A paper bill never goes through eTax, so it is not a backlog (ค้างส่ง) either.
export const PAPER_STATUS_META: StatusMeta = { label: "กระดาษ", color: "#64748B" };

// Unsent and past KTB's monthly deadline — same wording as the expired chip and filter.
export const EXPIRED_STATUS_META: StatusMeta = { label: "เลยกำหนดส่ง", color: "#6a6a6a" };

/** Label of the IN_PROGRESS bucket in filters and counts: it covers RECEIVE / SUBMIT_RD too. */
export const IN_PROGRESS_GROUP_LABEL = "กำลังดำเนินการ";

// ส่งไม่ได้ — the pre-submit gate refused the bill. Kept apart from ค้างส่ง on purpose:
// ค้างส่ง is a backlog someone can clear, this one never clears by being worked on. The
// buyer block is frozen on the bill, so correcting the customer master does not heal it —
// only re-issuing the document does.
const BLOCKED_META: StatusMeta = { label: "ส่งไม่ได้", color: "#FB3B3B" };

export const getEtaxStatusMeta = (
  status: ETaxDocumentStatus | null | undefined,
  eligible?: boolean | null,
  documentStatus?: ETaxKtbDocumentStatus | null,
  isPaper?: boolean,
): StatusMeta => {
  // A paper bill that was never sent is not ค้างส่ง, and the gate's verdict is moot for it.
  if (!status && isPaper) return PAPER_STATUS_META;
  // The verdict outranks "not sent" only when the bill has never been sent: a document
  // already at KTB keeps its real status even if a later re-check would refuse it.
  if (!status && eligible === false) return BLOCKED_META;
  if (!status) return NOT_SENT_META;
  // Prefer the lifecycle status when KTB has reported one — it is the same fact, said
  // precisely. Absence is itself informative here and must not be papered over: a sent
  // document with no lifecycle status is one the agent has never acknowledged.
  return (documentStatus && KTB_PROGRESS_META[documentStatus]) || ETAX_STATUS_META[status];
};

// ---- KTB document lifecycle (webhook `document_status`) ----
// Kept separate from the coarse E-Tax status above: several of these map to the same
// chip, but they are what tells the user how far the document actually got.

export const KTB_DOCUMENT_STATUS_LABEL: Record<ETaxKtbDocumentStatus, string> = {
  RECEIVE: "ตัวแทนได้รับแล้ว รอออกเอกสาร",
  SIGNED: "ออกเอกสารแล้ว",
  SIGNED_FAIL: "ลงลายมือชื่อไม่สำเร็จ",
  SUBMIT_RD: "ยื่นสรรพากรแล้ว รอผลตอบรับ",
  RD_ACCEPT: "กรมสรรพากรตอบรับ",
  RD_REJECT: "กรมสรรพากรปฏิเสธ",
  CANCEL: "ยกเลิกการนำส่งแล้ว",
};

export const getKtbDocumentStatusLabel = (
  status: ETaxKtbDocumentStatus | null | undefined,
): string => (status ? KTB_DOCUMENT_STATUS_LABEL[status] : "-");

/**
 * Tooltip for the signed-tax-invoice action. The signed file only exists once KTB has
 * signed the document, so an item that has not got there yet explains why rather than
 * offering a button that would 404.
 */
export const getSignedPdfTooltip = (
  status: ETaxKtbDocumentStatus | null | undefined,
): string => {
  if (!status) return "ยังไม่มีใบกำกับภาษีที่ลงลายมือชื่อ";
  if (status === "SIGNED_FAIL" || status === "CANCEL") {
    return KTB_DOCUMENT_STATUS_LABEL[status];
  }
  if (status === "RECEIVE") return "ยังไม่มีใบกำกับภาษีที่ลงลายมือชื่อ";
  return "ดูใบกำกับภาษีที่ลงลายมือชื่อ";
};

// ---- Withdrawing a document from the filing agent ----

/**
 * Whether the withdraw action is worth offering for a document, mirroring the backend gate
 * (`isWithdrawableDocumentStatus`). KTB remains the authority — a document may have moved
 * on since it last told us where it was — so this only decides whether the button is
 * offered, never whether the withdrawal succeeds. A refusal comes back in KTB's own words.
 *
 * A sent document with no lifecycle status counts as withdrawable: null means the agent has
 * never acknowledged it, which is where a stuck document sits, and those are precisely the
 * ones someone needs to pull back.
 */
const WITHDRAWABLE_STATUSES: ETaxKtbDocumentStatus[] = ["RECEIVE", "SIGNED"];

export const canWithdrawSubmission = (
  etaxStatus: ETaxDocumentStatus | null | undefined,
  documentStatus: ETaxKtbDocumentStatus | null | undefined,
  /** The row's `can_withdraw` from a newer backend; when present it decides alone. */
  canWithdraw?: boolean | null,
): boolean => {
  if (canWithdraw !== undefined && canWithdraw !== null) return canWithdraw === true;
  // Never sent, so there is nothing at the agent to pull back.
  if (!etaxStatus) return false;
  return !documentStatus || WITHDRAWABLE_STATUSES.includes(documentStatus);
};

/** Why this document cannot be withdrawn — the same reasons the backend would give. */
const WITHDRAW_REFUSAL: Partial<Record<ETaxKtbDocumentStatus, string>> = {
  SUBMIT_RD: "ยื่นกรมสรรพากรไปแล้ว ยกเลิกไม่ได้ ต้องออกใบลดหนี้แทน",
  RD_ACCEPT: "ยื่นกรมสรรพากรไปแล้ว ยกเลิกไม่ได้ ต้องออกใบลดหนี้แทน",
  CANCEL: "เอกสารนี้ถูกยกเลิกไปแล้ว",
  // Resending a rejected bill by hand is not allowed either (owner decision Q2).
  RD_REJECT: "เอกสารนี้ไม่ได้ถูกออก จึงไม่มีอะไรให้ยกเลิก ให้ออกเอกสารแบบกระดาษแทน",
  SIGNED_FAIL: "เอกสารนี้ไม่ได้ถูกออก จึงไม่มีอะไรให้ยกเลิก ให้ออกเอกสารแบบกระดาษแทน",
};

/**
 * Tooltip for the withdraw action. A disabled button with no explanation is the worst of
 * both worlds — it says an action exists and refuses to say why it is closed — so every
 * disabled state names its reason, and the one that matters most (already filed) names the
 * remedy too, because there is one and it is not this button.
 */
export const getWithdrawTooltip = (
  etaxStatus: ETaxDocumentStatus | null | undefined,
  documentStatus: ETaxKtbDocumentStatus | null | undefined,
  /** The row's `can_withdraw` from a newer backend. */
  canWithdraw?: boolean | null,
): string => {
  if (canWithdrawSubmission(etaxStatus, documentStatus, canWithdraw))
    return "ยกเลิกการนำส่งตัวแทน";
  if (!etaxStatus) return "บิลนี้ยังไม่ได้ส่ง จึงไม่มีอะไรให้ยกเลิก";
  const refusal = documentStatus && WITHDRAW_REFUSAL[documentStatus];
  if (refusal) return refusal;
  if (etaxStatus === "FAILED")
    return "ส่งไม่สำเร็จ ไม่มีเอกสารอยู่ที่ตัวแทนนำส่งให้ยกเลิก";
  // The backend refused although the status alone would allow it: KTB has not given the
  // item a transaction id yet, so there is nothing to address the withdrawal to.
  if (canWithdraw === false)
    return "ตัวแทนนำส่งยังไม่ได้ออกเลขอ้างอิงให้เอกสารนี้ จึงยังยกเลิกไม่ได้";
  return "สถานะปัจจุบันของเอกสารนี้ยกเลิกไม่ได้";
};

export const ETAX_STATUS_FILTER: { label: string; value: string }[] = [
  { label: "ทั้งหมด", value: "all" },
  { label: "ตัวแทนนำส่งได้รับแล้ว", value: "COMPLETED" },
  // Includes RECEIVE (รอออกเอกสาร) and SUBMIT_RD (รอผลตอบรับ) — see KTB_PROGRESS_META.
  { label: IN_PROGRESS_GROUP_LABEL, value: "IN_PROGRESS" },
  { label: "ค้างส่ง", value: "NOT_SENT" },
  { label: "ส่งไม่ได้", value: "BLOCKED" },
  { label: "ส่งไม่สำเร็จ", value: "FAILED" },
  // Weekly auto-send states. These cut across the send statuses above: a held or scheduled
  // bill is still ค้างส่ง, and a bill waiting for acknowledgement is one that failed.
  { label: "ไม่ส่งอัตโนมัติ", value: "HELD" },
  { label: "รอส่งตามรอบ", value: "SCHEDULED" },
  { label: "รอรับทราบ", value: "NEEDS_ACK" },
  // Failed because KTB could not be reached; the hourly sweep will send it again.
  { label: "กำลังลองส่งใหม่", value: "RETRYING" },
  // Unsent, but KTB's monthly deadline for its tax month has passed — paper invoice instead.
  { label: "เลยกำหนดส่ง", value: "EXPIRED" },
];

// A note has no hold, round or hourly retry (the notes list refuses those filters), and the notes
// list has no acknowledge action to go with รอรับทราบ.
const NOTE_UNSUPPORTED_STATUS_FILTERS = ["HELD", "SCHEDULED", "RETRYING", "NEEDS_ACK"];

export const NOTE_ETAX_STATUS_FILTER = ETAX_STATUS_FILTER.filter(
  (item) => !NOTE_UNSUPPORTED_STATUS_FILTERS.includes(item.value),
);

/** The notes list opens on the notes still owed to e-Tax, as the bill send page does. */
export const NOTE_DEFAULT_STATUS_FILTER = "NOT_SENT";

/** A status filter value from a URL, or "all" when it is not one this list offers. */
export const resolveEtaxStatusFilter = (value: string | null | undefined): string =>
  value && ETAX_STATUS_FILTER.some((item) => item.value === value) ? value : "all";
